/* eslint-disable @typescript-eslint/no-explicit-any */
import { msalInstance, scopes } from "./msal-config"
import { toast } from "react-toastify";

interface ProblemDetails {
  type?: string; // URI reference for the error type
  title?: string; // Short summary of the problem
  status?: number; // HTTP status code
  detail?: string; // Human-readable explanation of the error
  instance?: string; // URI reference for the specific occurrence
  [key: string]: any; // Allow additional custom fields
}

interface ValidationProblemDetails extends ProblemDetails {
  errors?: Record<string, string[]>;
  message?: string;
}

export class ValidationError extends Error {
  public errors: Record<string, string>;

  constructor(message: string, errors: Record<string, string>) {
    super(message); // Pass the message to the base Error class
    this.name = "ValidationError"; // Set the error name
    this.errors = errors; // Store the formatted errors
    // Ensure the correct prototype chain for instanceof checks
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class ProblemError extends Error {
  public title: string | null;
  public detail: string | null;

  constructor(message: string, title: string | null, detail: string | null) {
    super(message); // Pass the message to the base Error class
    this.title = title;
    this.detail = detail;
    this.name = "ProblemError"; // Set the error name
    // Ensure the correct prototype chain for instanceof checks
    Object.setPrototypeOf(this, ProblemError.prototype);
  }
}

class HttpClient {
  private baseURL: string

  constructor() {
    if (typeof window == "undefined") {
      console.warn('no window found seting baseURL')
      this.baseURL = ''
      return
    }

    const origin = window.location.origin
    if (origin.indexOf('localhost') > 0) {
      this.baseURL = 'https://localhost:7245'
      return
    } else if (origin.indexOf('biogain-dev-app') > 0 || origin.indexOf('dev.app.biogain.ai') > 0) {
      this.baseURL = 'https://biogain-dev-api.azurewebsites.net'
      return
    } else if (origin.indexOf('biogain-prod-app') > 0 || origin.indexOf('app.biogain.ai') > 0) {
      this.baseURL = 'https://biogain-prod-api.azurewebsites.net'
      return
    }
    
    throw new Error(`Cannot find api endpoint for origin ${origin}`)
  }

  private async getAuthToken(): Promise<string> {
    try {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length === 0) {
        throw new Error("No authenticated user found")
      }

      const silentRequest = {
        scopes: scopes,
        account: accounts[0],
      }

      const response = await msalInstance.acquireTokenSilent(silentRequest)
      return response.accessToken
    } catch (error) {
      console.error("Failed to acquire token:", error)
      throw new Error("Authentication failed")
    }
  }

  private async uploadFileRequeset<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const token = await this.getAuthToken()

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      // Handle empty responses
      if (response.status === 204) {
        return {} as T
      }

      return await response.json()
    } catch (error) {
      console.error(`HTTP request failed: ${endpoint}`, error)
      throw error
    }
  }

  private async downloadFileRequeset(endpoint: string, options: RequestInit = {}): Promise<void> {
    try {
      const token = await this.getAuthToken()

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      })

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'downloaded-file.xlsx'; // Fallback filename

      if (contentDisposition) {
        // Try to match filename* (UTF-8 encoded)
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(;|$)/);
        if (filenameStarMatch && filenameStarMatch[1]) {
          // Decode URI-encoded filename from filename*
          filename = decodeURIComponent(filenameStarMatch[1]);
        } else {
          // Fallback to filename parameter
          const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
          if (filenameMatch && filenameMatch[1]) {
            // Trim quotes and whitespace
            filename = filenameMatch[1].replace(/^"|"$/g, '').trim();
          }
        }
      }

      // Get the file as a Blob
      const fileBlob = await response.blob();

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(fileBlob);

      // Create a temporary <a> element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.toLowerCase(); // Use the extracted filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`HTTP request failed: ${endpoint}`, error)
      throw error
    }
  }


  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const token = await this.getAuthToken()

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      })

      if (!response.ok) {
        let errorData: ProblemDetails | ValidationProblemDetails = {};

        try {
          errorData = await response.json();
        } catch {
          // Handle JSON parsing failure
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

       if (response.status === 400 && "errors" in errorData && errorData.errors) {
          // Handle ValidationProblemDetails for status 400
          const formattedErrors = Object.entries(errorData.errors).reduce(
            (acc, [key, messages]) => {
              // Type guard to ensure messages is string[]
              if (Array.isArray(messages) && messages.every((msg) => typeof msg === "string")) {
                return { ...acc, [key]: messages.join(", ") };
              }
              return acc; // Skip invalid entries
            },
            {} as Record<string, string>
          );
          throw new ValidationError(
            errorData.message || "Validation failed",
            formattedErrors
          );
        }
        toast.error(`Error: ${errorData.title || response.statusText}`);
        // Handle generic ProblemDetails for other statuses
        throw new ProblemError(errorData.message || errorData.detail, errorData.title ?? null, errorData.detail ?? null);
      }

      // Handle empty responses
      if (response.status === 204) {
        return {} as T
      }

      return await response.json()
    } catch (error) {
      console.error(`HTTP request failed: ${endpoint}`, error)
      throw error
    }
  }

  async upload<T>(endpoint: string, file: any): Promise<T> {
    return this.uploadFileRequeset<T>(endpoint, {
      method: "POST",
      body: file,
    })
  }

  async download(endpoint: string, data: any): Promise<void> {
    return this.downloadFileRequeset(endpoint, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" })
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    })
  }  

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" })
  }
}

export const httpClient = new HttpClient()
